import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const featureItems = [
  {
    title: 'Strategy setup',
    body: 'Configure Luno API keys, choose a market pair, set mock trading, and tune strategy values from one desktop workspace.',
  },
  {
    title: 'Rules engine',
    body: 'Build active BUY, SELL, and HOLD rules with salience, named checks, indicator signals, and reusable rule references.',
  },
  {
    title: 'Backtesting',
    body: 'Run the same strategy rules against historical candle data before allowing the system to operate live.',
  },
  {
    title: 'Live monitoring',
    body: 'Track trader state, last action, prices, bank value, signal history, and audit events while the loop is running.',
  },
];

const statItems = [
  { label: 'Platform', value: 'Windows' },
  { label: 'Trading modes', value: 'Mock + Live' },
  { label: 'Market focus', value: 'Luno' },
];

const screenshotItems = [
  {
    title: 'Welcome',
    src: '/trader/home.PNG',
    alt: 'Trader for Luno welcome screen',
  },
  {
    title: 'Dashboard',
    src: '/trader/dashboard.PNG',
    alt: 'Trader for Luno dashboard screen',
  },
  {
    title: 'Config',
    src: '/trader/config.PNG',
    alt: 'Trader for Luno config screen',
  },
  {
    title: 'Backtest Results',
    src: '/trader/backtest.PNG',
    alt: 'Trader for Luno backtest results screen',
  },
];

const ScreenshotFrame = ({ title, src, alt, large = false }) => (
  <Paper
    elevation={0}
    sx={{
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid rgba(16, 35, 63, 0.12)',
      boxShadow: large ? '0 28px 70px rgba(16, 35, 63, 0.16)' : '0 18px 42px rgba(16, 35, 63, 0.1)',
      backgroundColor: '#ffffff',
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid rgba(16, 35, 63, 0.08)',
      }}
    >
      <Box component="img" src="/luno.png" alt="" sx={{ width: 18, height: 18, objectFit: 'contain' }} />
      <Typography sx={{ color: 'primary.main', fontWeight: 900, fontSize: '0.9rem' }}>
        {title}
      </Typography>
    </Box>
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        display: 'block',
        width: '100%',
        aspectRatio: large ? '16 / 9.4' : '16 / 10',
        objectFit: 'contain',
        objectPosition: 'top left',
      }}
    />
  </Paper>
);

const MiniChart = ({ variant = 'line' }) => (
  <Box
    sx={{
      position: 'relative',
      height: 92,
      overflow: 'hidden',
      border: '1px solid rgba(16, 35, 63, 0.12)',
      background:
        'repeating-linear-gradient(0deg, rgba(16,35,63,0.06), rgba(16,35,63,0.06) 1px, transparent 1px, transparent 22px), repeating-linear-gradient(90deg, rgba(16,35,63,0.05), rgba(16,35,63,0.05) 1px, transparent 1px, transparent 42px), #ffffff',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: '18px 12px 20px',
        clipPath:
          variant === 'bars'
            ? 'polygon(0 70%, 8% 44%, 16% 66%, 24% 28%, 32% 52%, 40% 18%, 48% 42%, 56% 24%, 64% 62%, 72% 35%, 80% 58%, 88% 32%, 100% 50%, 100% 100%, 0 100%)'
            : 'polygon(0 68%, 8% 52%, 15% 60%, 23% 34%, 31% 43%, 39% 22%, 48% 30%, 56% 50%, 65% 38%, 74% 57%, 83% 44%, 91% 64%, 100% 54%, 100% 100%, 0 100%)',
        background:
          variant === 'bars'
            ? 'linear-gradient(180deg, rgba(32,198,183,0.55), rgba(55,118,212,0.12))'
            : 'linear-gradient(180deg, rgba(55,118,212,0.55), rgba(32,198,183,0.12))',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        left: 12,
        right: 12,
        top: variant === 'bars' ? 34 : 48,
        height: 3,
        backgroundColor: variant === 'bars' ? '#20c6b7' : '#3776d4',
        transform: 'rotate(-4deg)',
        transformOrigin: 'left center',
      }}
    />
  </Box>
);

const AppFrame = ({ title, eyebrow, children }) => (
  <Paper
    elevation={0}
    sx={{
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid rgba(16, 35, 63, 0.12)',
      boxShadow: '0 24px 58px rgba(16, 35, 63, 0.12)',
      backgroundColor: '#f4f6f9',
    }}
  >
    <Box
      sx={{
        height: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(16, 35, 63, 0.08)',
      }}
    >
      <Box component="img" src="/luno.png" alt="" sx={{ width: 16, height: 16, objectFit: 'contain' }} />
      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#10233f' }}>
        Trader for Luno
      </Typography>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '82px 1fr', sm: '120px 1fr' }, minHeight: 280 }}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#1d2838',
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.78rem', sm: '1rem' }, lineHeight: 1.15, mb: 3 }}>
          Trader for Luno
        </Typography>
        {['Dashboard', 'Config', 'Backtest Results'].map((item) => (
          <Typography key={item} sx={{ fontSize: { xs: '0.63rem', sm: '0.72rem' }, fontWeight: 700, mb: 2.1 }}>
            {item}
          </Typography>
        ))}
        <Box sx={{ mt: 'auto' }}>
          <Typography sx={{ fontSize: '0.58rem', textAlign: 'center', opacity: 0.8 }}>Version 2.0.2</Typography>
        </Box>
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2.4 }, minWidth: 0 }}>
        <Typography sx={{ color: '#54657d', fontSize: '0.7rem', mb: 0.5 }}>{eyebrow}</Typography>
        <Typography sx={{ color: '#061326', fontSize: { xs: '1.2rem', sm: '1.45rem' }, fontWeight: 900, mb: 1.8 }}>
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  </Paper>
);

const ConfigPreview = () => (
  <AppFrame title="Config Screen" eyebrow="Edit config values and strategy rules">
    <Grid container spacing={1}>
      <Grid item xs={12} sm={4}>
        <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff' }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, mb: 1 }}>Settings</Typography>
          {['Enable Mock Trade', 'API Key ID', 'PAIR XBTZAR', 'CANDLE_SECONDS', 'BACKTEST_START_ZAR'].map((item) => (
            <Box key={item} sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800 }}>{item}</Typography>
              <Box sx={{ height: 12, border: '1px solid rgba(16,35,63,0.24)', mt: 0.3 }} />
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff', minHeight: 204 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 900 }}>BUY / SELL Rules</Typography>
            <Stack direction="row" spacing={0.5}>
              {['Add', 'Edit', 'Delete'].map((item, index) => (
                <Box
                  key={item}
                  sx={{
                    px: 0.8,
                    py: 0.45,
                    color: '#fff',
                    backgroundColor: index === 2 ? '#ef4444' : index === 1 ? '#f59e0b' : '#2463e8',
                    fontSize: '0.52rem',
                    fontWeight: 900,
                  }}
                >
                  {item}
                </Box>
              ))}
            </Stack>
          </Stack>
          {['strong_bullish_entry', 'bearish_reversal', 'stop_loss_exit', 'bearish_profit_exit'].map((rule, index) => (
            <Box
              key={rule}
              sx={{
                display: 'grid',
                gridTemplateColumns: '44px 54px 1fr 1.6fr',
                gap: 1,
                py: 0.5,
                borderTop: index === 0 ? '1px solid rgba(16,35,63,0.12)' : 0,
              }}
            >
              {['Yes', index < 2 ? 'BUY' : 'SELL', rule, 'MACD > signal AND live profit check'].map((cell) => (
                <Typography key={cell} noWrap sx={{ fontSize: '0.56rem' }}>
                  {cell}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Grid>
    </Grid>
  </AppFrame>
);

const BacktestPreview = () => (
  <AppFrame title="Backtest Results" eyebrow="Audit log, price graph, and bank graph">
    <Grid container spacing={1}>
      {['Start Bank R 10000.00', 'End Bank R 10332.09', 'PnL R 332.09', 'Trade Count 11'].map((item) => {
        const [first, second, ...rest] = item.split(' ');
        return (
          <Grid item xs={6} sm={3} key={item}>
            <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff' }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#54657d', fontWeight: 900 }}>{`${first} ${second}`}</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 900 }}>{rest.join(' ')}</Typography>
            </Box>
          </Grid>
        );
      })}
      <Grid item xs={12} sm={7}>
        <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff', minHeight: 166 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, mb: 0.8 }}>Audit Log Table</Typography>
          {['BUY 1237156.0 MOCK strong_bullish_entry', 'SELL 1267535.0 MOCK bearish_profit_exit', 'BUY 1326517.0 MOCK strong_bullish_entry'].map((row) => (
            <Box key={row} sx={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr 1.8fr', gap: 1, py: 0.45 }}>
              {row.split(' ').map((cell) => (
                <Typography key={cell} sx={{ fontSize: '0.56rem' }}>
                  {cell}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item xs={12} sm={5}>
        <Stack spacing={1}>
          <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, mb: 0.8 }}>Price Over Time</Typography>
            <MiniChart variant="bars" />
          </Box>
          <Box sx={{ p: 1, border: '1px solid rgba(16,35,63,0.22)', backgroundColor: '#fff' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, mb: 0.8 }}>Bank Value</Typography>
            <MiniChart />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  </AppFrame>
);

const Trader = () => (
  <Box
    sx={{
      overflow: 'hidden',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(243,247,251,0.98) 54%, #ffffff 100%)',
    }}
  >
    <Box component="section" sx={{ pt: { xs: 6, md: 9 }, pb: { xs: 5, md: 8 } }}>
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 4, lg: 6 }} alignItems="center">
          <Grid item xs={12} lg={5}>
            <Stack spacing={2.4}>
              <Chip
                label="Trader for Luno"
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: 'rgba(32, 198, 183, 0.14)',
                  color: 'secondary.main',
                  fontWeight: 800,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  color: 'primary.main',
                  fontSize: { xs: '2.45rem', sm: '3.5rem', md: '4.4rem' },
                  maxWidth: 720,
                }}
              >
                Build, backtest, and monitor Luno trading strategies.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', md: '1.13rem' },
                  maxWidth: 650,
                }}
              >
                A Windows desktop trader for configuring strategy rules, testing those rules
                against historical Luno data, and watching live trader activity from one focused
                control surface.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" href="/#contact" sx={{ backgroundColor: '#14325c' }}>
                  Request the Windows app
                </Button>
                <Button variant="outlined" href="#features" sx={{ borderColor: 'rgba(20,50,92,0.28)' }}>
                  View features
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={7}>
            <ScreenshotFrame
              title="Dashboard"
              src="/trader/dashboard.PNG"
              alt="Trader for Luno dashboard showing live trader controls, signal explorer, and audit events"
              large
            />
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Container maxWidth="xl">
      <Grid container spacing={2.5} sx={{ mb: { xs: 5, md: 8 } }}>
        {statItems.map((item) => (
          <Grid item xs={6} md={3} key={item.label}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 2,
                border: '1px solid rgba(16, 35, 63, 0.08)',
                backgroundColor: '#ffffff',
              }}
            >
              <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', fontWeight: 700 }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: 'primary.main', fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 900 }}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>

    <Box id="features" component="section" sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={4}>
            <Typography
              variant="h2"
              sx={{ color: 'primary.main', fontSize: { xs: '2rem', md: '3rem' }, mb: 2 }}
            >
              Practical controls for the full strategy loop.
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.08rem' } }}>
              Trader for Luno keeps setup, rule design, testing, and operational monitoring close
              together so the system can be reviewed before and during live trading.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2.5}>
              {featureItems.map((feature) => (
                <Grid item xs={12} sm={6} key={feature.title}>
                  <Box
                    sx={{
                      height: '100%',
                      p: { xs: 2.2, md: 2.8 },
                      borderRadius: 2,
                      border: '1px solid rgba(16, 35, 63, 0.1)',
                      backgroundColor: '#f8fbfe',
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 900, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>{feature.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box component="section" sx={{ py: { xs: 5, md: 9 } }}>
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 4, md: 5 }}>
          <Stack spacing={1.5} sx={{ maxWidth: 820 }}>
            <Typography
              variant="overline"
              sx={{ color: 'secondary.main', letterSpacing: '0.16em', fontWeight: 800 }}
            >
              Screens
            </Typography>
            <Typography variant="h2" sx={{ color: 'primary.main', fontSize: { xs: '2rem', md: '3.2rem' } }}>
              Desktop views for setup, testing, and running.
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {screenshotItems.map((screenshot) => (
              <Grid item xs={12} md={6} key={screenshot.title}>
                <ScreenshotFrame {...screenshot} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="xl">
      <Divider />
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box component="img" src="/luno.png" alt="Luno logo" sx={{ width: 54, height: 54, objectFit: 'contain' }} />
          <Box>
            <Typography sx={{ color: 'primary.main', fontSize: '1.25rem', fontWeight: 900 }}>
              Ready to see Trader for Luno?
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Contact QuantumMind Software to discuss access, setup, and licensing.
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" href="/#contact" sx={{ backgroundColor: '#20c6b7', color: '#08233b' }}>
          Contact us
        </Button>
      </Box>
    </Container>
  </Box>
);

export default Trader;
